<script lang="ts">
export const elCalendarCardColors = [
  'purple',
  'orange',
  'brandBlue',
  'green',
  'rose',
  'indigo',
  'red',
  'pink',
  'cyan',
  'sapphire',
  'teal',
  'yellow',
  'gray',
] as const;
export type ElCalendarCardColor = (typeof elCalendarCardColors)[number];

export const elCalendarCardStatuses = ['confirmed', 'to-be-confirmed', 'past'] as const;
export type ElCalendarCardStatus = (typeof elCalendarCardStatuses)[number];
</script>

<script setup lang="ts">
withDefaults(
  defineProps<{
    height?: number;
    color: ElCalendarCardColor;
    status?: ElCalendarCardStatus;
    primaryRow?: string;
    secondaryRow?: string;
    tertiaryRow?: string;
    horizontalPaddingLeft?: boolean;
    cursor?: 'default' | 'pointer';
  }>(),
  {
    height: 50,
    color: 'sapphire',
    status: 'past',
    label: undefined,
    primaryRow: undefined,
    secondaryRow: undefined,
    tertiaryRow: undefined,
    horizontalPaddingLeft: true,
    cursor: 'pointer',
  },
);

const confirmedBackgroundColors = {
  purple: 'bg-purple-500',
  orange: 'bg-orange-500',
  brandBlue: 'bg-brandBlue-500',
  green: 'bg-green-500',
  rose: 'bg-rose-500',
  indigo: 'bg-indigo-500',
  red: 'bg-red-500',
  pink: 'bg-pink-500',
  cyan: 'bg-cyan-500',
  sapphire: 'bg-brandBlue-500',
  teal: 'bg-teal-500',
  yellow: 'bg-yellow-500',
  gray: 'bg-neutral-500',
} as const;

const pastBackgroundColors = {
  purple: 'bg-purple-300',
  orange: 'bg-orange-300',
  brandBlue: 'bg-brandBlue-300',
  green: 'bg-green-300',
  rose: 'bg-rose-300',
  indigo: 'bg-indigo-300',
  red: 'bg-red-300',
  pink: 'bg-pink-300',
  cyan: 'bg-cyan-300',
  sapphire: 'bg-brandBlue-300',
  teal: 'bg-teal-300',
  yellow: 'bg-yellow-300',
  gray: 'bg-neutral-300',
} as const;

const toBeConfirmedStrokeColors = {
  purple: 'text-purple-500 border-purple-500',
  orange: 'text-orange-500 border-orange-500',
  brandBlue: 'text-brandBlue-500 border-brandBlue-500',
  green: 'text-green-500 border-green-500',
  rose: 'text-rose-500 border-rose-500',
  indigo: 'text-indigo-500 border-indigo-500',
  red: 'text-red-500 border-red-500',
  pink: 'text-pink-500 border-pink-500',
  cyan: 'text-cyan-500 border-cyan-500',
  sapphire: 'text-brandBlue-500 border-brandBlue-500',
  teal: 'text-teal-500 border-teal-500',
  yellow: 'text-yellow-500 border-yellow-500',
  gray: 'text-neutral-500 border-neutral-500',
} as const;
</script>

<template>
  <div class="-ml-1 -mr-1 pr-0.5" :class="{ 'pl-3': horizontalPaddingLeft }" :style="{ height: `${Math.max(12, height)}px` }">
    <div
      class="grid max-w-full border rounded h-full"
      :class="{
        'cursor-pointer': cursor === 'pointer',
        'cursor-default': cursor === 'default',
        'rounded-sm': height < 32,
        rounded: height >= 32,
        [confirmedBackgroundColors[color]]: status === 'confirmed',
        [pastBackgroundColors[color]]: status === 'past',
        'bg-white': status === 'to-be-confirmed',
        'text-white': status === 'confirmed' || status === 'past',
        'border-white': status === 'confirmed' || status === 'past',
        [toBeConfirmedStrokeColors[color]]: status === 'to-be-confirmed',
        'border-dashed': status === 'to-be-confirmed',
      }"
    >
      <div class="overflow-hidden">
        <div
          class="flex flex-col justify-center text-xs px-1 leading-none"
          :class="{
            'py-2': height >= 48,
            'gap-1': height >= 32 && height < 48,
            'gap-2': height >= 48 && height < 96,
            'gap-3': height >= 96,
          }"
        >
          <span v-if="primaryRow" class="font-semibold truncate">
            {{ primaryRow }}
          </span>
          <span v-if="secondaryRow" class="font-semibold truncate">
            {{ secondaryRow }}
          </span>
          <span v-if="tertiaryRow" class="truncate">
            {{ tertiaryRow }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
